import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, Modal, Input, message, Space, Card, Descriptions, Upload } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';

const USER_API = axios.create({ baseURL: 'http://localhost:8089/api', timeout: 15000 });

const DOC_LABELS: Record<string, string> = {
  SA_ID: '🪪 SA ID / Passport',
  PASSPORT: '🪪 Passport',
  PROOF_OF_ADDRESS: '🏠 Proof of Address',
  BUSINESS_REG: '📄 CIPC Registration',
  TRADE_LICENSE: '🔧 Trade License',
};

export default function Verification() {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [providerId, setProviderId] = useState<string>('');
  const [docType, setDocType] = useState<string>('SA_ID');
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    USER_API.get('/users/documents/pending').then(r => setDocs(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const review = (docId: number, status: string) => {
    USER_API.patch(`/users/documents/${docId}/review`, { status, notes, reviewedBy: '0' })
      .then(() => { message.success(`Document ${status.toLowerCase()}`); setReviewModal(null); setNotes(''); load(); })
      .catch(() => message.error('Review failed'));
  };

  const handleManualUpload = () => {
    if (!providerId) {
      message.error("Provider ID is required");
      return;
    }
    
    setUploading(true);
    // Simulating file upload to backend
    USER_API.post(`/users/${providerId}/documents`, {
      documentType: docType,
      fileName: `admin_uploaded_${docType}.pdf`,
      fileData: `placeholder_url_from_s3`,
    })
    .then(() => {
      message.success("Document uploaded successfully on behalf of provider");
      setUploadModal(false);
      setProviderId('');
      load();
    })
    .catch(err => {
      console.error(err);
      message.error("Failed to upload document");
    })
    .finally(() => setUploading(false));
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Provider ID', dataIndex: 'providerId', width: 100 },
    { title: 'Type', dataIndex: 'documentType', render: (v: string) => DOC_LABELS[v] || v },
    { title: 'File', dataIndex: 'fileName' },
    { title: 'Status', dataIndex: 'reviewStatus', render: (v: string) => (
      <Tag color={v === 'APPROVED' ? 'green' : v === 'REJECTED' ? 'red' : 'orange'}>{v}</Tag>
    )},
    { title: 'Uploaded', dataIndex: 'uploadedAt', render: (v: string) => v ? new Date(v).toLocaleString() : '—' },
    {
      title: 'Actions', render: (_: any, r: any) => r.reviewStatus === 'PENDING' ? (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setReviewModal(r)}>Review</Button>
        </Space>
      ) : '—',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card 
        title={`📋 Pending Document Reviews (${docs.length})`} 
        style={{ marginBottom: 24 }}
        extra={
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadModal(true)}>
            Upload for Provider
          </Button>
        }
      >
        <Table dataSource={docs} columns={columns} rowKey="id" loading={loading} size="middle" pagination={{ pageSize: 15 }} />
      </Card>

      {/* Manual Upload Modal for Admin */}
      <Modal
        title="Upload Document for Provider"
        open={uploadModal}
        onCancel={() => setUploadModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setUploadModal(false)}>Cancel</Button>,
          <Button key="upload" type="primary" loading={uploading} onClick={handleManualUpload}>
            Upload Document
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Provider ID:</label>
            <Input 
              value={providerId} 
              onChange={e => setProviderId(e.target.value)} 
              placeholder="e.g. 1" 
              type="number"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Document Type:</label>
            <select 
              value={docType} 
              onChange={e => setDocType(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d9d9d9' }}
            >
              {Object.entries(DOC_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginTop: 16 }}>
            <Upload maxCount={1} beforeUpload={() => false}>
              <Button icon={<UploadOutlined />}>Select File (PDF/Image)</Button>
            </Upload>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
              Note: This will simulate uploading the file directly into the provider's account.
            </div>
          </div>
        </Space>
      </Modal>

      <Modal
        title="Review Document"
        open={!!reviewModal}
        onCancel={() => { setReviewModal(null); setNotes(''); }}
        footer={null}
        width={600}
      >
        {reviewModal && (
          <>
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Provider ID">{reviewModal.providerId}</Descriptions.Item>
              <Descriptions.Item label="Document Type">{DOC_LABELS[reviewModal.documentType] || reviewModal.documentType}</Descriptions.Item>
              <Descriptions.Item label="File Name">{reviewModal.fileName}</Descriptions.Item>
              <Descriptions.Item label="Uploaded">{new Date(reviewModal.uploadedAt).toLocaleString()}</Descriptions.Item>
            </Descriptions>

            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 16, textAlign: 'center', color: '#999' }}>
              📎 Document preview will be available when file storage (S3) is integrated.
              <br />For now, verify using the provider's submitted details.
            </div>

            <Input.TextArea
              placeholder="Review notes (optional)..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              style={{ marginBottom: 16 }}
            />

            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => review(reviewModal.id, 'REJECTED')}>
                Reject
              </Button>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => review(reviewModal.id, 'APPROVED')}>
                Approve
              </Button>
            </Space>
          </>
        )}
      </Modal>
    </div>
  );
}
